import React from 'react';
import { 
    Star, MapPin, ThumbsUp, ThumbsDown, DollarSign, 
    Wifi, Car, Utensils, Dumbbell, Waves, Coffee,
    PlaneTakeoff, Sparkles, Users, ExternalLink, Globe,
    Heart, ShieldCheck
} from 'lucide-react';

const AMENITY_ICONS = {
    'pool': Waves, 'swimming': Waves,
    'wifi': Wifi, 'internet': Wifi,
    'parking': Car, 'valet': Car,
    'restaurant': Utensils, 'dining': Utensils,
    'gym': Dumbbell, 'fitness': Dumbbell,
    'spa': Sparkles, 'wellness': Sparkles,
    'breakfast': Coffee,
    'shuttle': PlaneTakeoff, 'airport': PlaneTakeoff,
    'pet': Heart, 'family': Users,
};

const getAmenityIcon = (text) => {
    const lower = text.toLowerCase();
    for (const [key, Icon] of Object.entries(AMENITY_ICONS)) {
        if (lower.includes(key)) return Icon;
    }
    return ShieldCheck;
};

const SECTION_CONFIG = {
    'star rating':     { color: '#f59e0b', bg: '#fffbeb' },
    'location':        { color: '#3b82f6', bg: '#eff6ff' },
    'review scores':   { color: '#8b5cf6', bg: '#f5f3ff' },
    'top upsides':     { color: '#10b981', bg: '#ecfdf5' },
    'top downsides':   { color: '#ef4444', bg: '#fef2f2' },
    'pricing':         { color: '#f59e0b', bg: '#fffbeb' },
    'key amenities':   { color: '#06b6d4', bg: '#ecfeff' },
    'best for':        { color: '#ec4899', bg: '#fdf2f8' },
};

const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s,)]+)|(\b[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s,)]*)?)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        const raw = match[0];
        const href = raw.startsWith('http') ? raw : `https://${raw}`;
        parts.push(
            <a key={match.index} href={href} target="_blank" rel="noopener noreferrer"
               onClick={(e) => e.stopPropagation()}
               className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium">
                {raw}
            </a>
        );
        lastIndex = match.index + raw.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 1 ? parts : text;
};

const parseSummary = (summary) => {
    if (!summary) return { overview: '', sections: [] };
    const lines = summary.split('\n').filter(l => l.trim());
    const sections = [];
    let overview = [];
    let currentSection = null;

    for (const line of lines) {
        const trimmed = line.trim();
        const lowerLine = trimmed.toLowerCase();

        if (lowerLine.startsWith('why this matters')) {
            const content = trimmed.includes(':') ? trimmed.split(':').slice(1).join(':').trim() : '';
            currentSection = { key: 'why_this_matters', content: content ? [content] : [] };
            sections.push(currentSection);
            continue;
        }

        let matched = false;
        for (const sectionKey of Object.keys(SECTION_CONFIG)) {
            if (lowerLine.startsWith(sectionKey + ':') || lowerLine === sectionKey) {
                const content = trimmed.includes(':') ? trimmed.split(':').slice(1).join(':').trim() : '';
                currentSection = { key: sectionKey, content: content ? [content] : [] };
                sections.push(currentSection);
                matched = true;
                break;
            }
        }
        if (matched) continue;

        if (currentSection) {
            currentSection.content.push(trimmed);
        } else {
            overview.push(trimmed);
        }
    }

    return { overview: overview.join(' '), sections };
};

// Extract a numeric score from text like "8.5/10" or "4.2/5"
const extractScore = (text) => {
    const match = text.match(/(\d+\.?\d*)\s*\/\s*(\d+)/);
    if (match) {
        const score = parseFloat(match[1]);
        const max = parseFloat(match[2]);
        return { score, max, normalized: max === 5 ? score * 2 : score };
    }
    return null;
};

const ScoreBar = ({ label, score, max }) => {
    const normalized = max === 5 ? (score / 5) * 100 : (score / 10) * 100;
    const color = normalized >= 80 ? '#10b981' : normalized >= 60 ? '#f59e0b' : '#ef4444';
    return (
        <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-28 truncate">{label}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${normalized}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs font-semibold w-12 text-right" style={{ color }}>{score}/{max}</span>
        </div>
    );
};

const HotelAnalysisView = ({ summary, url }) => {
    const { overview, sections } = parseSummary(summary);
    const domain = (() => { try { return new URL(url).hostname.replace('www.', ''); } catch { return url; } })();

    const renderSection = (section, idx) => {
        const config = SECTION_CONFIG[section.key];
        if (!config) return null;

        const bullets = [];
        const paragraphs = [];
        for (const line of section.content) {
            if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
                bullets.push(line.replace(/^[•\-*]\s*/, ''));
            } else if (line.trim()) {
                paragraphs.push(line);
            }
        }
        const allText = section.content.join(' ');

        // Review scores - special rendering with score bars
        if (section.key === 'review scores') {
            const scoreLines = bullets.length > 0 ? bullets : section.content;
            const scores = scoreLines.map(line => {
                const s = extractScore(line);
                const platform = line.split(':')[0]?.replace(/^[•\-*]\s*/, '').trim();
                return s ? { platform, ...s } : null;
            }).filter(Boolean);

            return (
                <div key={idx} className="rounded-xl border border-border/60 overflow-hidden" data-testid="section-review-scores">
                    <div className="px-4 py-3 flex items-center gap-2.5" style={{ backgroundColor: config.bg }}>
                        <Star className="w-4 h-4" style={{ color: config.color }} />
                        <span className="text-sm font-semibold" style={{ color: config.color }}>Review Scores</span>
                    </div>
                    <div className="px-4 py-3 space-y-2.5 bg-white">
                        {scores.map((s, i) => (
                            <ScoreBar key={`score-${s.platform}`} label={s.platform} score={s.score} max={s.max} />
                        ))}
                        {paragraphs.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-2 italic">{paragraphs.join(' ')}</p>
                        )}
                    </div>
                </div>
            );
        }

        // Upsides - green theme
        if (section.key === 'top upsides') {
            return (
                <div key={idx} className="rounded-xl border border-emerald-200/60 overflow-hidden" data-testid="section-top-upsides">
                    <div className="px-4 py-3 bg-emerald-50 flex items-center gap-2.5">
                        <ThumbsUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-700">Top Upsides</span>
                    </div>
                    <div className="px-4 py-3 bg-white space-y-2">
                        {bullets.map((b, i) => (
                            <div key={`pro-${i}-${b.slice(0,15)}`} className="flex items-start gap-2.5 text-sm">
                                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-emerald-600 text-xs font-bold">+</span>
                                </div>
                                <span className="text-foreground/80">{linkify(b)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Downsides - red theme
        if (section.key === 'top downsides') {
            return (
                <div key={idx} className="rounded-xl border border-red-200/60 overflow-hidden" data-testid="section-top-downsides">
                    <div className="px-4 py-3 bg-red-50 flex items-center gap-2.5">
                        <ThumbsDown className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-semibold text-red-600">Top Downsides</span>
                    </div>
                    <div className="px-4 py-3 bg-white space-y-2">
                        {bullets.map((b, i) => (
                            <div key={`pro-${i}-${b.slice(0,15)}`} className="flex items-start gap-2.5 text-sm">
                                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-red-500 text-xs font-bold">&minus;</span>
                                </div>
                                <span className="text-foreground/80">{linkify(b)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Key amenities - badge/chip style
        if (section.key === 'key amenities') {
            const amenities = bullets.length > 0 ? bullets : allText.split(',').map(a => a.trim()).filter(Boolean);
            return (
                <div key={idx} className="rounded-xl border border-border/60 overflow-hidden" data-testid="section-key-amenities">
                    <div className="px-4 py-3 flex items-center gap-2.5" style={{ backgroundColor: config.bg }}>
                        <Sparkles className="w-4 h-4" style={{ color: config.color }} />
                        <span className="text-sm font-semibold" style={{ color: config.color }}>Amenities</span>
                    </div>
                    <div className="px-4 py-3 bg-white flex flex-wrap gap-2">
                        {amenities.map((a, i) => {
                            const Icon = getAmenityIcon(a);
                            return (
                                <span key={`amenity-${a}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-50 border border-cyan-200/60 text-cyan-700 text-xs font-medium">
                                    <Icon className="w-3 h-3" />
                                    {a}
                                </span>
                            );
                        })}
                    </div>
                </div>
            );
        }

        // Pricing - special icon
        if (section.key === 'pricing') {
            return (
                <div key={idx} className="rounded-xl border border-amber-200/60 overflow-hidden" data-testid="section-pricing">
                    <div className="px-4 py-3 bg-amber-50 flex items-center gap-2.5">
                        <DollarSign className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-700">Pricing</span>
                    </div>
                    <div className="px-4 py-3 bg-white space-y-2">
                        {paragraphs.map((p, i) => (
                            <p key={`p-${i}-${p.slice(0,15)}`} className="text-sm text-foreground/80 leading-relaxed">{linkify(p)}</p>
                        ))}
                        {bullets.map((b, i) => (
                            <div key={`b${i}`} className="flex items-start gap-2.5 text-sm">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                                <span className="text-foreground/80">{linkify(b)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Location - with map pin
        if (section.key === 'location') {
            return (
                <div key={idx} className="rounded-xl border border-blue-200/60 overflow-hidden" data-testid="section-location">
                    <div className="px-4 py-3 bg-blue-50 flex items-center gap-2.5">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Location</span>
                    </div>
                    <div className="px-4 py-3 bg-white">
                        <p className="text-sm text-foreground/80 leading-relaxed">{linkify(allText)}</p>
                    </div>
                </div>
            );
        }

        // Generic section (star rating, best for)
        const icons = {
            'star rating': Star,
            'best for': Users,
        };
        const Icon = icons[section.key] || Globe;

        return (
            <div key={idx} className="rounded-xl border border-border/60 overflow-hidden" data-testid={`section-${section.key.replace(/\s+/g, '-')}`}>
                <div className="px-4 py-3 flex items-center gap-2.5" style={{ backgroundColor: config.bg }}>
                    <Icon className="w-4 h-4" style={{ color: config.color }} />
                    <span className="text-sm font-semibold capitalize" style={{ color: config.color }}>{section.key}</span>
                </div>
                <div className="px-4 py-3 bg-white space-y-2">
                    {paragraphs.map((p, i) => (
                        <p key={`p-${i}-${p.slice(0,15)}`} className="text-sm text-foreground/80 leading-relaxed">{linkify(p)}</p>
                    ))}
                    {bullets.map((b, i) => (
                        <div key={`b${i}`} className="flex items-start gap-2.5 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: config.color }} />
                            <span className="text-foreground/80">{linkify(b)}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Separate upsides and downsides for side-by-side rendering
    const upsides = sections.find(s => s.key === 'top upsides');
    const downsides = sections.find(s => s.key === 'top downsides');
    const otherSections = sections.filter(s => s.key !== 'top upsides' && s.key !== 'top downsides' && s.key !== 'why_this_matters');
    const whyMatters = sections.find(s => s.key === 'why_this_matters');

    return (
        <div className="space-y-4" data-testid="hotel-analysis-view">
            {/* Header badge */}
            <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/60 text-amber-700 text-xs font-semibold tracking-wide uppercase">
                    <Star className="w-3 h-3" />
                    Hotel Analysis
                </div>
                <span className="text-xs text-muted-foreground">via {domain}</span>
            </div>

            {/* Overview */}
            {overview && (
                <p className="text-foreground/80 leading-relaxed text-[15px]">{linkify(overview)}</p>
            )}

            {/* Sections */}
            <div className="grid gap-4">
                {otherSections.map((section, idx) => renderSection(section, idx))}
            </div>

            {/* Upsides & Downsides side by side on desktop */}
            {(upsides || downsides) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {upsides && renderSection(upsides, 'up')}
                    {downsides && renderSection(downsides, 'down')}
                </div>
            )}

            {/* Why This Matters */}
            {whyMatters && (
                <div className="rounded-xl p-4 border-l-4 border-amber-400 bg-amber-50/50" data-testid="why-this-matters">
                    <p className="text-xs font-mono uppercase tracking-wider text-amber-700 mb-1.5 font-semibold">Why Bookmark This</p>
                    <p className="text-foreground/80 leading-relaxed text-sm">{linkify(whyMatters.content.join(' '))}</p>
                </div>
            )}

            {/* Book button */}
            <div className="pt-2">
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors shadow-md shadow-amber-500/20"
                   data-testid="book-now-btn">
                    <ExternalLink className="w-4 h-4" />
                    View on {domain}
                </a>
            </div>
        </div>
    );
};

export default HotelAnalysisView;
